<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import {
		Modal,
		Container,
		Text,
		Button,
		Input,
		Loader,
		Icon,
		ImageGroup,
	} from '@Shelly/shelly-design-system';

	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { format } from 'svelte-i18n';
	import { postReq } from 'src/services/http';
	import { toast } from '@Shelly/shelly-design-system';
	import { ICON_GROUPS } from 'src/constants';
	const dispatch = createEventDispatcher();

	export let showModal = false;

	let windowInnerWidth: number;
	$: isMobile = windowInnerWidth <= SystemScreenBreakpoints.MOBILE_MAX;

	let searchQuery = '';
	let searchResults: string[] = [];
	let isSearching = false;
	let selectedIconInModal = '';
	let searchTimeout: ReturnType<typeof setTimeout>;

	$: searchResultImages = searchResults.map((iconClass, i) => ({
		id: i,
		icon: iconClass,
		selected: iconClass === selectedIconInModal,
	}));

	$: groupImages = ICON_GROUPS.map((group) => ({
		...group,
		label: $format(group.label.key, { default: group.label.default }),
		images: group.icons.map((iconClass, i) => ({
			id: i,
			icon: iconClass,
			selected: iconClass === selectedIconInModal,
		})),
	}));

	const showErrorToast = () => {
		toast.open({
			type: 'warning',
			title: $format('toast_error_load_icons', {
				default: 'Failed to load icons. Please try again.',
			}),
		});
	};

	const searchFontAwesomeIcons = async (query: string) => {
		if (!query?.trim()) {
			searchResults = [];
			isSearching = false;
			return;
		}

		try {
			isSearching = true;
			const response = await postReq('/interface/device/get_af', {
				search: query.trim().toLowerCase(),
				limit: 50,
			});

			if (response?.isok && response.data) {
				searchResults = response.data.map((icon) => `fat fa-${icon.id}`);
			} else {
				showErrorToast();
			}
		} catch (err) {
			console.error('Search icons error:', err);
			showErrorToast();
			searchResults = [];
		} finally {
			isSearching = false;
		}
	};

	const handleSearch = (e: Event) => {
		const query = (e.target as HTMLInputElement).value;
		clearTimeout(searchTimeout);

		if (!query?.trim()) {
			searchResults = [];
			isSearching = false;
			return;
		}

		isSearching = true;
		searchTimeout = setTimeout(() => searchFontAwesomeIcons(query), 1000);
	};

	const onImageGroupClick = (e: CustomEvent) => {
		selectedIconInModal = e.detail.icon;
	};

	const addSelectedIcon = () => {
		if (!selectedIconInModal) return;
		dispatch('icon-selected', { icon: selectedIconInModal });
		closeModal();
	};

	const closeModal = () => {
		dispatch('close');
		searchQuery = '';
		searchResults = [];
		selectedIconInModal = '';
	};

	$: hasResults = !!(searchQuery?.trim() && searchResults.length > 0);
	$: noResultsFromSearch = !!(searchQuery?.trim() && searchResults.length === 0 && !isSearching);
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

<Modal
	{showModal}
	title={$format('title_find_icon', { default: 'Find an Icon to Add' })}
	on:close={closeModal}
	class="icon-search-modal"
	size="lg"
	removeScrollPadding
	preserveMaxHeight
	customContent={noResultsFromSearch || isSearching}
>
	<Input
		slot="toolbar"
		type="text"
		label={$format('text_search_for_icon_placeholder', {
			default: "Search for an icon (e.g. 'home', 'energy')...",
		})}
		icon="fas fa-search"
		size={isMobile ? 'm' : 'l'}
		bind:value={searchQuery}
		on:input={handleSearch}
		autofocus
		id="icon-search-input"
		aria-labelledby="icon-search-input"
	/>

	<svelte:fragment slot="content">
		{#if noResultsFromSearch || isSearching}
			{#if isSearching}
				<div
					class="d-flex flex-direction-column justify-content-center align-items-center flex-1 min-height-0"
				>
					<Loader class="mb-xs" />
					<Text>
						{$format('text_searching_for_icons', { default: 'Searching for icons...' })}
					</Text>
				</div>
			{:else}
				<div
					class="d-flex flex-direction-column justify-content-center align-items-center text-center flex-1 min-height-0"
				>
					<Icon icon="fa-light fa-magnifying-glass" size="l" class="mb-m" />
					<Text class="text-bold">
						{$format('no_results_for_search_param', {
							default: `No results for '{search}'`,
							values: { search: searchQuery },
						})}
					</Text>
					<Text styleType="body-small" class="mt-s text-color-muted">
						{$format('label_check_spelling_try_search', {
							default: 'Check the spelling or try a new search.',
						})}
					</Text>
				</div>
			{/if}
		{:else}
			<Container padding="scrollPaddings" noScroll>
				<Text
					styleType="body-small"
					class="text-color-muted text-center mb-l mt-sm-s mt-md-0"
					noWrap
				>
					{#if hasResults}
						{$format('text_icon_search_results_count', {
							default: `{count} results found for „ {searchQuery} “`,
							values: { count: searchResults.length, searchQuery },
						})}
					{:else}
						{$format('title_search_hunderts_icon', {
							default: 'Search through hundreds of icons',
						})}
					{/if}
				</Text>

				{#if hasResults}
					<ImageGroup
						expandedByDefault
						images={searchResultImages}
						on:click={onImageGroupClick}
					/>
				{:else}
					{#each groupImages as group, i}
						<ImageGroup
							title={group.label}
							class={i < groupImages.length - 1 ? 'mb-xl' : ''}
							images={group.images}
							on:click={onImageGroupClick}
						/>
					{/each}
				{/if}
			</Container>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="footer">
		<div class="d-flex justify-content-end">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				variant="secondary"
				responsiveSize={{ sm: 'm' }}
				on:click={closeModal}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>

			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="primary"
				responsiveSize={{ sm: 'm' }}
				disabled={!selectedIconInModal}
				on:click={addSelectedIcon}
			>
				{$format('add_icon_button', { default: 'Add Icon' })}
			</Button>
		</div>
	</svelte:fragment>
</Modal>
